import React, {useMemo, useState} from 'react'
import {useLocation, useHistory} from 'react-router-dom'
import {
    Box,
    Flex,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Select,
    TabList,
    Tab,
    Tabs
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import StyledContent from '../../../leap/components/styled-content'

export const FAQAccordion = ({data}) => {
    const location = useLocation()
    const history = useHistory()
    const [accordionIndex, setAccordionIndex] = useState(0)

    const selectedCategoryIdx = useMemo(() => {
        const searchParams = new URLSearchParams(location.search)
        const categoryId = searchParams.get('category')
        return categoryId ? data.findIndex((item) => item.id === categoryId) : 0
    }, [location.search, data])

    const handleCategoryClick = (category) => {
        if (category.id !== data[selectedCategoryIdx].id) {
            const newParams = new URLSearchParams(location.search)
            newParams.set('category', category.id)
            history.push({search: newParams.toString()})
            setAccordionIndex(0)
            window.scrollTo(0, 0)
        }
    }

    const filteredItems = useMemo(() => {
        return data[selectedCategoryIdx]?.items?.filter((item) => item?.name?.trim()) || []
    }, [data, selectedCategoryIdx])

    return (
        <Flex
            gap={8}
            direction={{base: 'column', md: 'row'}}
            backgroundColor="stokkeCore.white"
            padding={8}
            marginX={-8}
            width="100%"
        >
            <Box maxW="343px" w="100%">
                <Text as="h2" textStyle="Regular 2XLarge" pb={2}>
                    <FormattedMessage defaultMessage="FAQs" id="customer_service.faq.faqs" />
                </Text>
                <Select
                    onChange={(e) => {
                        handleCategoryClick(data[Number(e.target.value)])
                    }}
                    value={selectedCategoryIdx}
                    display={{base: 'block', md: 'none'}}
                    data-testid="accordion-select"
                >
                    {data.map((category, idx) => (
                        <option key={idx} value={idx}>
                            {category.name.length > 31
                                ? `${category.name.slice(0, 31)}…`
                                : category.name}
                        </option>
                    ))}
                </Select>
                <Tabs
                    display={{base: 'none', md: 'block'}}
                    data-testid="accordion-list"
                    orientation="vertical"
                    variant="subtle"
                    onChange={(index) => handleCategoryClick(data[index])}
                >
                    <TabList>
                        {data?.map((category) => (
                            <Tab
                                key={category.id}
                                justifyContent="start"
                                textAlign="start"
                                aria-current={data[selectedCategoryIdx]?.id === category?.id}
                                color={
                                    data[selectedCategoryIdx]?.id === category?.id
                                        ? 'stokkeCore.white'
                                        : 'stokkeCore.black'
                                }
                                sx={{
                                    backgroundImage:
                                        'linear-gradient(to bottom, transparent 50%, var(--chakra-colors-stokkeCore-black) 50%)',
                                    backgroundSize: '100% 210%',
                                    backgroundPosition: '0 0',
                                    transition: 'background-position .5s ease, color .5s ease'
                                }}
                                _hover={{
                                    backgroundPosition: '0 97%',
                                    color: 'stokkeCore.white'
                                }}
                                {...(data[selectedCategoryIdx]?.id === category?.id && {
                                    bg: 'stokkeCore.black',
                                    color: 'stokkeCore.white'
                                })}
                            >
                                {category?.name}
                            </Tab>
                        ))}
                    </TabList>
                </Tabs>
            </Box>

            <Box flex="1">
                <Text as="h2" mb={4} textStyle="Regular 2XLarge" data-testid="category-title">
                    {data[selectedCategoryIdx]?.name}
                </Text>
                <Accordion
                    allowToggle
                    key={data[selectedCategoryIdx]?.id}
                    index={accordionIndex}
                    onChange={setAccordionIndex}
                >
                    {filteredItems.map((item) => (
                        <AccordionItem key={item?.id} value={item?.id}>
                            <h3>
                                <AccordionButton>
                                    <Box flex="1" textAlign="left" textStyle="Regular XLarge">
                                        {item?.name}
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                            </h3>
                            <AccordionPanel pb={4}>
                                <StyledContent content={item?.description} />
                            </AccordionPanel>
                        </AccordionItem>
                    ))}
                </Accordion>
            </Box>
        </Flex>
    )
}

FAQAccordion.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    name: PropTypes.string.isRequired,
                    description: PropTypes.string
                })
            )
        })
    ).isRequired
}
